import { ReactNode } from 'react'
import { FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { StatusPill } from '@/components/common/StatusPill'
import { TONE_DOT_CLASS, type Tone as PillTone } from '@/lib/tone'
import { cn } from '@/lib/utils'

export type TabDef = { id: string; label: string; count?: number }

export function SegmentedTabs({ tabs, active, onChange }: { tabs: TabDef[]; active: string; onChange: (id: string) => void }) {
  return (
    <div role="tablist" className="inline-flex gap-0.5 rounded-lg border border-border bg-secondary p-0.5">
      {tabs.map(tab => {
        const isActive = tab.id === active
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            className={cn(
              'inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {tab.label}
            {tab.count != null && (
              <span className={cn('tnum grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-semibold', isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>
                {tab.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export function StatTile({
  label,
  icon,
  value,
  unit,
  sub,
  variant = 'default'
}: {
  label: string
  icon?: ReactNode
  value: ReactNode
  unit?: string
  sub?: ReactNode
  variant?: 'default' | 'accent' | 'danger'
}) {
  return (
    <div className="rounded-xl border border-border bg-background/60 p-3.5">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground [&_svg]:h-3.5 [&_svg]:w-3.5">
        {icon}
        {label}
      </p>
      <p className={cn('tnum mt-1 text-lg font-semibold', variant === 'accent' && 'text-primary', variant === 'danger' && 'text-destructive')}>
        {value}
        {unit && <small className="text-xs font-normal text-muted-foreground"> {unit}</small>}
      </p>
      {sub && <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  )
}

export type Tone = 'rent' | 'sale' | 'active' | 'closed' | 'expiring' | 'paid' | 'due' | 'late'

const PILL_TONE: Record<Tone, PillTone> = {
  rent: 'progress',
  sale: 'highlight',
  active: 'success',
  closed: 'neutral',
  expiring: 'warning',
  paid: 'success',
  due: 'info',
  late: 'danger'
}

export type ContractCardProps = {
  thumb?: string
  fallbackIcon?: ReactNode
  title: string
  type: { label: string; tone: Tone; icon?: ReactNode }
  status: { label: string; tone: Tone }
  term?: ReactNode
  amount: string
  unit?: string
  onView?: () => void
}

export function ContractCard({ thumb, fallbackIcon, title, type, status, term, amount, unit, onView }: ContractCardProps) {
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-3 py-3.5 first:pt-0 last:pb-0 sm:grid-cols-[auto_minmax(0,1fr)_auto]">
      <div className="grid h-12 w-14 shrink-0 place-items-center overflow-hidden rounded-xl border border-border bg-secondary text-muted-foreground [&_svg]:h-5 [&_svg]:w-5">
        {thumb ? <img src={thumb} alt="" className="h-full w-full object-cover" /> : fallbackIcon}
      </div>
      <div className="min-w-0">
        <p className="line-clamp-2 text-sm font-semibold leading-snug">{title}</p>
        <div className="my-1.5 flex flex-wrap gap-1.5">
          <StatusPill tone={PILL_TONE[type.tone]} label={type.label} />
          <StatusPill tone={PILL_TONE[status.tone]} label={status.label} />
        </div>
        {term && <p className="flex items-center gap-1.5 text-xs text-muted-foreground [&_svg]:h-3.5 [&_svg]:w-3.5">{term}</p>}
      </div>
      <div className="col-span-2 flex items-center justify-between gap-2 sm:col-span-1 sm:flex-col sm:items-end sm:text-right">
        <p className="tnum text-sm font-semibold">
          {amount}
          {unit && <small className="text-[11px] font-normal text-muted-foreground">{unit}</small>}
        </p>
        {onView && (
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={onView}>
            <FileText className="h-3.5 w-3.5" />
            Ver contrato
          </Button>
        )}
      </div>
    </div>
  )
}

export type PaymentRowProps = {
  state: 'paid' | 'due' | 'late'
  competence: string
  subref?: string
  method?: string
  methodIcon?: ReactNode
  due: string
  dueNote?: string
  amount: string
  badge: { label: string; tone: Tone }
}

const STATE_TONE: Record<PaymentRowProps['state'], PillTone> = {
  paid: 'success',
  due: 'info',
  late: 'danger'
}

export function PaymentRow({ state, competence, subref, method, methodIcon, due, dueNote, amount, badge }: PaymentRowProps) {
  return (
    <div className="grid grid-cols-[10px_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-b border-border py-3 text-[13px] last:border-0 sm:grid-cols-[10px_minmax(0,1.4fr)_minmax(0,1fr)_auto_auto] sm:gap-4">
      <span className={cn('h-2 w-2 justify-self-center rounded-full', TONE_DOT_CLASS[STATE_TONE[state]], state === 'late' && 'ring-[3px] ring-destructive/20')} />
      <div className="min-w-0">
        <p className="truncate font-medium">{competence}</p>
        {subref && <p className="truncate text-[11px] text-muted-foreground">{subref}</p>}
      </div>
      <div className="hidden min-w-0 items-center gap-1.5 text-muted-foreground sm:flex [&_svg]:h-3.5 [&_svg]:w-3.5">
        {methodIcon}
        <span className="truncate">{method}</span>
      </div>
      <div className="col-start-2 text-muted-foreground sm:col-start-auto sm:text-right">
        {due}
        {dueNote && <span className="block text-[11px]">{dueNote}</span>}
      </div>
      <div className="col-start-3 row-start-1 flex flex-col items-end gap-1 sm:col-start-auto sm:row-start-auto sm:flex-row sm:items-center sm:gap-3">
        <span className="tnum text-right font-semibold sm:min-w-[88px]">{amount}</span>
        <StatusPill tone={PILL_TONE[badge.tone]} label={badge.label} />
      </div>
    </div>
  )
}

export function PaymentsFooter({ label, total, tone }: { label: string; total: string; tone?: 'due' | 'late' }) {
  return (
    <div className="mt-1 flex items-center justify-between border-t border-border pt-3">
      <span className="text-[12.5px] text-muted-foreground">{label}</span>
      <span className={cn('tnum text-base font-semibold', tone === 'due' && 'text-info', tone === 'late' && 'text-destructive')}>{total}</span>
    </div>
  )
}
